'use client';

import { Fragment, type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { VolunteerSlotCard } from '@sola/ark-skippy/molecules/volunteer-slot-card/volunteer-slot-card.tsx';
import type { GraphQLVolunteerSlot } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { VolunteerSlotContact } from '@sola/shared-utils/supporters/volunteers.ts';
import {
  absoluteUrlFromPath,
  getVolunteerEventPath,
} from '@sola/skippy-sdk/urls.ts';
import { formatPostalAddress } from '@sola/utils/geo.ts';

import { LocalAlertsProvider } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import { VolunteerActionDialog } from '../../molecules/volunteer-action-dialog/volunteer-action-dialog.tsx';
import { VolunteerEventInfo } from '../../molecules/volunteer-event-info/volunteer-event-info.tsx';
import { useVolunteerEventsLogic } from './volunteer-events.logic.ts';
import { VolunteerNoEvents } from './volunteer-no-events.tsx';
import { VolunteerSkeleton } from './volunteer-skeleton.tsx';

const VolunteerEventsWrapper: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="VolunteerEvents max-w-md flex flex-col gap-comfy-xl mt-comfy-xl">
    {children}
  </div>
);

interface VolunteerSlotCardWithDialogsProps {
  contacts: VolunteerSlotContact[];
  endTime: Date | string;
  slots: GraphQLVolunteerSlot[];
  startTime: Date | string;
  status: GraphQLVolunteerSlot['status'];
  title: string;
  onCancel: (slots: GraphQLVolunteerSlot[]) => Promise<void> | void;
  onConfirm: (slots: GraphQLVolunteerSlot[]) => Promise<void> | void;
}

const VolunteerSlotCardWithDialogs: React.FC<
  VolunteerSlotCardWithDialogsProps
> = ({
  contacts,
  endTime,
  slots,
  startTime,
  status,
  title,
  onCancel,
  onConfirm,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  return (
    <>
      <VolunteerSlotCard
        contacts={contacts}
        endTime={endTime}
        onCancel={() => setIsCancelDialogOpen(true)}
        onConfirm={() => setIsConfirmDialogOpen(true)}
        startTime={startTime}
        status={status}
        title={title}
      />
      <VolunteerActionDialog
        action="cancel"
        contacts={contacts}
        onOpenChange={setIsCancelDialogOpen}
        onSubmit={onCancel}
        open={isCancelDialogOpen}
        slots={slots}
      />
      <VolunteerActionDialog
        action="confirm"
        contacts={contacts}
        onOpenChange={setIsConfirmDialogOpen}
        onSubmit={onConfirm}
        open={isConfirmDialogOpen}
        slots={slots}
      />
    </>
  );
};

export const VolunteerEventsContent = () => {
  const { t } = useTranslation(['volunteers', 'my']);
  const {
    handleCancelSlot,
    handleConfirmSlot,
    isPending,
    isError,
    volunteerEvents,
  } = useVolunteerEventsLogic();

  // Skeleton on initial load only. `isFetching` is intentionally excluded so
  // background refetches don't flash the whole view back to the skeleton while
  // stale data is available.
  if (isPending) {
    return (
      <VolunteerEventsWrapper>
        <VolunteerSkeleton />
      </VolunteerEventsWrapper>
    );
  }

  if (isError) {
    return (
      <VolunteerEventsWrapper>
        <Alert
          action={
            <Link href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </Link>
          }
          message={t('volunteers:myEvents.requestError.message')}
          title={t('volunteers:myEvents.requestError.title')}
          variant="negative"
        />
      </VolunteerEventsWrapper>
    );
  }

  if (volunteerEvents.length === 0) {
    return (
      <VolunteerEventsWrapper>
        <div className="flex justify-center">
          <VolunteerNoEvents />
        </div>
      </VolunteerEventsWrapper>
    );
  }

  return (
    <VolunteerEventsWrapper>
      <LocalAlerts className="flex flex-col gap-comfy-sm" />

      {volunteerEvents.map((event, eventIndex) => {
        const {
          cancellableSlots,
          contacts,
          endDate,
          id,
          locationName,
          slotGroups,
          slots,
          startDate,
          venue,
        } = event;
        const { address1, address2, city, country, name, postalCode, state } =
          venue ?? {};

        return (
          <Fragment key={id}>
            <section className="flex flex-col gap-comfy-xl">
              <VolunteerEventInfo
                address={formatPostalAddress(
                  address1,
                  address2,
                  city,
                  state,
                  postalCode,
                  country,
                )}
                contacts={contacts}
                endDate={new Date(endDate)}
                eventDetailUrl={absoluteUrlFromPath(
                  getVolunteerEventPath(locationName, id),
                )}
                locationName={name ?? undefined}
                onCancel={handleCancelSlot}
                shiftCount={slots.length}
                slots={cancellableSlots}
                startDate={new Date(startDate)}
                title={locationName}
              />

              <div className="grid grid-cols-1 gap-comfy-sm md:grid-cols-2">
                {slotGroups.map(
                  ({
                    contacts: groupContacts,
                    endDate: slotEndDate,
                    slots: groupedSlots,
                    startDate: slotStartDate,
                    status,
                    title,
                  }) => (
                    <VolunteerSlotCardWithDialogs
                      contacts={groupContacts}
                      endTime={slotEndDate}
                      key={groupedSlots
                        .map(({ id: slotId }) => slotId)
                        .join(',')}
                      onCancel={handleCancelSlot}
                      onConfirm={handleConfirmSlot}
                      slots={groupedSlots}
                      startTime={slotStartDate}
                      status={status}
                      title={title}
                    />
                  ),
                )}
              </div>
            </section>

            {eventIndex < volunteerEvents.length - 1 && (
              <Divider purpose="neutral" />
            )}
          </Fragment>
        );
      })}
    </VolunteerEventsWrapper>
  );
};

export const VolunteerEvents = () => {
  return (
    <LocalAlertsProvider>
      <VolunteerEventsContent />
    </LocalAlertsProvider>
  );
};
